import { WhistleIcon } from '../../components/WhistleIcon';
import type { SportEventTypeConfig } from '../types';
import { COMMON_EVENT_TYPES } from '../common';

/**
 * NHL play-by-play event catalog. Owns the filter pills, icons, and display
 * names for every hockey event type emitted by SES. The cross-sport `custom`
 * type is appended from COMMON_EVENT_TYPES (shared with every sport);
 * `period_start`/`period_end` still live inline here since they're rendered
 * identically for both sports today.
 */
export const NHL_EVENT_TYPES: ReadonlyArray<SportEventTypeConfig> = [
  ...COMMON_EVENT_TYPES,
  {
    type: 'goal',
    fetchFromBackend: true,
    showFilterPill: true,
    displayName: 'Goal',
    description: 'Goal scored',
  },
  {
    type: 'shot',
    fetchFromBackend: true,
    showFilterPill: true,
    displayName: 'Shot',
    description: 'Shot on goal',
  },
  {
    type: 'shot_on_goal',
    fetchFromBackend: true,
    showFilterPill: true,
    displayName: 'Shot on Goal',
    description: 'Shot on goal',
  },
  {
    type: 'blocked_shot',
    fetchFromBackend: false,
    showFilterPill: false,
    displayName: 'Blocked Shot',
    description: 'Shot blocked',
  },
  {
    type: 'missed_shot',
    fetchFromBackend: true,
    showFilterPill: false,
    displayName: 'Missed Shot',
    description: 'Shot missed',
  },
  {
    type: 'period_start',
    fetchFromBackend: true,
    showFilterPill: false,
    displayName: 'Period Start',
    hideTeamLogo: true,
  },
  {
    type: 'period_end',
    fetchFromBackend: false,
    showFilterPill: false,
    displayName: 'Period End',
    hideTeamLogo: true,
  },
  {
    type: 'penalty',
    fetchFromBackend: true,
    showFilterPill: true,
    displayName: 'Penalty',
    description: 'Penalty called',
  },
  {
    type: 'giveaway',
    fetchFromBackend: false,
    showFilterPill: false,
    displayName: 'Giveaway',
    description: 'Possession lost',
  },
  {
    type: 'icing',
    fetchFromBackend: true,
    showFilterPill: true,
    displayName: 'Icing',
    description: 'Icing',
    icon: WhistleIcon,
  },
  {
    type: 'offside',
    fetchFromBackend: true,
    showFilterPill: true,
    displayName: 'Offside',
    description: 'Offside',
    icon: WhistleIcon,
  },
  {
    type: 'faceoff',
    fetchFromBackend: true,
    showFilterPill: true,
    displayName: 'Faceoff',
    description: 'Faceoff',
  },
  {
    type: 'goalie_save',
    fetchFromBackend: true,
    showFilterPill: true,
    displayName: 'Saves',
    description: 'Goalie save',
  },
  {
    type: 'powerplay_start',
    fetchFromBackend: true,
    showFilterPill: true,
    displayName: 'Power Play',
    description: 'Power play started',
  },
  {
    type: 'hit',
    fetchFromBackend: true,
    showFilterPill: true,
    displayName: 'Hit',
    description: 'Body check',
  },
  {
    type: 'pass',
    fetchFromBackend: false, // Don't fetch - too many events
    showFilterPill: false,
    displayName: 'Passes',
  },
  {
    type: 'timeout',
    fetchFromBackend: false,
    showFilterPill: false,
    displayName: 'Timeouts',
  },
  {
    type: 'fight',
    fetchFromBackend: false,
    showFilterPill: false,
    displayName: 'Fights',
  },
  {
    type: 'shootout_goal',
    fetchFromBackend: true,
    showFilterPill: true,
    displayName: 'Shootout Goal',
    description: 'Shootout goal scored',
  },
  {
    type: 'shift',
    fetchFromBackend: true,
    showFilterPill: false,
    displayName: 'Shift',
    description: 'Player shift',
  },
];
